import * as React from 'react'

import {emptyArray, emptyObject} from '../../../../../utils/empty'
import type {KeyValue, WritableKeyValue} from '../../../../../utils/object'
import {keyValue} from '../../../../../utils/object'
import {mapItemWithSubtree} from '../AgentPoolPage.selectors'
import type {
  AgentPoolProjectsTreeProjectItem,
  ProjectEdge,
  ProjectsConnectionEdges,
} from '../AgentPoolPage.types'

type UnassignedProjectState = {
  edge: ProjectEdge
  item: AgentPoolProjectsTreeProjectItem
  recursively: boolean
}

type UnassignedProjectsState = KeyValue<string, UnassignedProjectState>
type WritableUnassignedProjectsState = WritableKeyValue<string, UnassignedProjectState>

export function useUnassignedProjectsState(
  agentPoolRawId: string,
  projectEdges: ProjectsConnectionEdges | undefined,
): [
  UnassignedProjectsState,
  (projectItem: AgentPoolProjectsTreeProjectItem, recursively: boolean) => void,
  (projectRawId: string) => void,
] {
  const [unassignedProjectEdges, setUnassignedProjectEdges] =
    React.useState<UnassignedProjectsState>(emptyObject)

  const cleanupUnassignedProjectEdge = React.useCallback(
    (projectRawId: string) => {
      if (unassignedProjectEdges[projectRawId] != null) {
        const newState: WritableUnassignedProjectsState = {
          ...unassignedProjectEdges,
        }
        delete newState[projectRawId]
        setUnassignedProjectEdges(newState)
      }
    },
    [unassignedProjectEdges],
  )

  const persistUnassignedProjectEdge = React.useCallback(
    (projectItem: AgentPoolProjectsTreeProjectItem, recursively: boolean) => {
      const projectEdge = projectEdges?.find(({node}) => node.rawId === projectItem.rawId)

      if (projectEdge != null) {
        const newState: UnassignedProjectsState = {
          ...unassignedProjectEdges,
          ...keyValue(projectItem.rawId, {
            edge: {
              node: {
                ...projectEdge.node,
                agentPools: {
                  edges:
                    projectEdge.node.agentPools?.edges.filter(
                      ({node: {rawId}}) => rawId !== agentPoolRawId,
                    ) || emptyArray,
                },
              },
            },
            item: mapItemWithSubtree(projectItem, item => ({
              ...item,
              agentPools: item.agentPools.filter(({rawId}) => rawId !== agentPoolRawId),
            })),
            recursively,
          }),
        }
        setUnassignedProjectEdges(newState)
      }
    },
    [agentPoolRawId, projectEdges, unassignedProjectEdges],
  )

  React.useEffect(() => {
    if (
      projectEdges != null &&
      projectEdges.length > 0 &&
      Object.keys(unassignedProjectEdges).length > 0
    ) {
      projectEdges.forEach(edge => {
        if (unassignedProjectEdges[edge.node.rawId] != null) {
          cleanupUnassignedProjectEdge(edge.node.rawId)
        }
      })
    }
  }, [cleanupUnassignedProjectEdge, projectEdges, unassignedProjectEdges])

  return [unassignedProjectEdges, persistUnassignedProjectEdge, cleanupUnassignedProjectEdge]
}
